import { Col, Flex, Form, FormInstance, Input, Row, Spin } from 'antd';
import { Container, Hint, Section, SectionTitle } from '../style';
import ImageUpload from '../../../../components/imageUpload';
import { useEffect, useRef, useState } from 'react';
import { fetchMachines } from '../../../../slices/machines';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../store';
import { File, Template, UploadBusinessType } from '../../../../types';
import {
  fetchLinkedMachines,
  updateDraftTemplate,
} from '../../../../slices/templates';
import { createTemplate, updateTemplate } from '../../../../utils/httpRequest';
import LinkedMachine from '../../components/linkedMachine';

const TemplateInformation = ({
  template,
  form,
  templateId,
}: {
  template: Template;
  form: FormInstance;
  templateId?: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState<File[]>(template?.images || []);
  const imagesRef = useRef<File[]>(template?.images || []);

  const handleLinkMachines = async () => {
    if (isUploading) {
      return false;
    }
    try {
      setLoading(true);
      await form.validateFields();
      const newTemplate = {
        ...template,
        ...form.getFieldsValue(),
        images: imagesRef.current,
        documents: template.documents || [],
      };
      const res = templateId
        ? await updateTemplate(newTemplate)
        : await createTemplate(newTemplate);
      dispatch(
        updateDraftTemplate({
          ...template,
          ...res.data,
        })
      );
      return true;
    } catch (error) {
      console.log(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploaded = (response: any) => {
    const raw = response?.data?.[0];
    const file = raw
      ? { id: raw.id, url: raw.fileUrl || raw.url, filename: raw.filename }
      : undefined;
    const newImages = file ? [file] : [];
    setImages(newImages);
    imagesRef.current = newImages;
    dispatch(
      updateDraftTemplate({
        ...template,
        images: newImages,
      })
    );
  };

  useEffect(() => {
    dispatch(fetchMachines());
  }, []);

  useEffect(() => {
    if (templateId) {
      dispatch(fetchLinkedMachines(templateId));
    }
  }, [templateId]);

  return (
    <Container>
      <Spin fullscreen spinning={loading} />
      <SectionTitle>Template Information</SectionTitle>
      <Section>
        <Row gutter={[80, 16]}>
          <Col span={8}>
            <ImageUpload
              businessType={UploadBusinessType.TEMPLATE}
              imageUrl={images?.[0]?.url}
              fixedWidth={false}
              onUploaded={handleImageUploaded}
              onLoadingChange={setIsUploading}
            />
          </Col>
          <Col span={16}>
            <Flex vertical>
              <Form.Item
                label="Template Name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: 'Please input Template Name!',
                  },
                ]}
              >
                <Input type="text" placeholder="Enter" />
              </Form.Item>
              <Form.Item label="Template Type" name="type">
                <Input type="text" placeholder="Enter" />
              </Form.Item>
              <Form.Item label={t('manufacturer')} name="manufacturer">
                <Input type="text" placeholder="Enter" />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Section>
      <LinkedMachine
        templateID={template.templateID}
        validateLinkMachine={handleLinkMachines}
        header={
          <Hint>
            Select the machine(s) you would like to link to this template *
          </Hint>
        }
      />
    </Container>
  );
};

export default TemplateInformation;
